import { useState, useEffect, useMemo } from "react";
import { filterEligibleListings } from "../../utils/openHouseHelpers";
import {
  createOpenHouse,
  updateOpenHouse,
  getEligibleListingsForOpenHouse,
  getOutsideHostingAgents,
} from "../../utils/firebasefunctions";
import { useStateValue } from "../../context/StateProvider";
import { eligibleHostingAgents } from "../../utils/openHouseHosts";
import { toast } from "react-toastify";
import {
  composeUpdateSellerEmail,
  composeUpdateAgentEmail,
} from "../../utils/openHouseEmailComposer";
import { sendOpenHouseEmail } from "../../utils/emailSender";
import { sendSchedulingEmails, sendListingAgentCopy } from "../../utils/openHouseSend";

export default function ScheduleModal({
  open,
  onClose,
  existing,           // null for new; existing OH doc for edit
  lockListing,        // optional deal to pre-select and lock (from deal-detail tab)
  onSaved,            // (oh) => void, called after successful save
}) {
  const [{ user, users, emailTemplates }] = useStateValue();
  const [eligibleDeals, setEligibleDeals] = useState([]);
  const [dealId, setDealId] = useState("");
  // hostingAgentId is the team-member's user id when the host is on the team.
  // "" (empty) means "outside agent" — name/email are typed freely.
  const [hostingAgentId, setHostingAgentId] = useState("");
  const [hostingAgentName, setHostingAgentName] = useState("");
  const [hostingAgentEmail, setHostingAgentEmail] = useState("");
  // Previously-used outside agents, loaded on modal open. Drives an
  // autocomplete on the name field so the team doesn't re-type them.
  const [pastHosts, setPastHosts] = useState([]);

  // Team members eligible to host an open house: active Admins + Employees.
  // Shared with the kiosk's "who's hosting?" gate so the two never drift.
  const teamHosts = useMemo(() => eligibleHostingAgents(users), [users]);

  const setHostFromTeamMember = (u) => {
    setHostingAgentId(u.id);
    setHostingAgentName(`${u.firstname || ""} ${u.lastname || ""}`.trim());
    setHostingAgentEmail(u.email || "");
  };
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Load eligible listings each time the modal opens (data freshness)
    getEligibleListingsForOpenHouse().then((deals) => {
      const eligible = filterEligibleListings(deals);
      eligible.sort((a, b) =>
        (a.propertyaddress || "").localeCompare(b.propertyaddress || "")
      );
      setEligibleDeals(eligible);
    });
    // Load previously-hosted outside agents for the name autocomplete.
    getOutsideHostingAgents().then(setPastHosts);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setDealId(existing.dealId);
      // Old OH docs may not have hostingAgentId — fall back to matching by
      // email so existing data lights up the team-member dropdown.
      const matchedByEmail =
        existing.hostingAgentId ||
        teamHosts.find(
          (u) =>
            u.email &&
            existing.hostingAgentEmail &&
            u.email.toLowerCase() === existing.hostingAgentEmail.toLowerCase(),
        )?.id ||
        "";
      setHostingAgentId(matchedByEmail);
      setHostingAgentName(existing.hostingAgentName || "");
      setHostingAgentEmail(existing.hostingAgentEmail || "");
      setDate(existing.date || "");
      setStartTime(existing.startTime || "");
      setEndTime(existing.endTime || "");
      setNotes(existing.notes || "");
    } else if (lockListing) {
      setDealId(lockListing.id);
      setHostingAgentId("");
      setHostingAgentName("");
      setHostingAgentEmail("");
      setDate("");
      // Default to a 12:00–2:00 PM open house so the user only changes
      // these when the slot is different. Minutes stay at :00 by default.
      setStartTime("12:00");
      setEndTime("14:00");
      setNotes("");
    } else {
      setDealId("");
      setHostingAgentId("");
      setHostingAgentName("");
      setHostingAgentEmail("");
      setDate("");
      setStartTime("12:00");
      setEndTime("14:00");
      setNotes("");
    }
  }, [open, existing, lockListing, teamHosts]);

  const selectedDeal = useMemo(
    () => lockListing || eligibleDeals.find((d) => d.id === dealId),
    [dealId, eligibleDeals, lockListing]
  );

  // Occupancy lives on the deal/listing as "Yes"/"No" (see seller-form.jsx).
  // When occupied, we save the OH but hold notifications until the user
  // confirms the seller verbally OK'd the time — see the banner below.
  const isOccupied =
    (selectedDeal?.occupied || "").toString().trim().toLowerCase() === "yes";
  // When editing an OH that's still waiting on seller approval, keep the
  // hold in place (the user can release it later via "Send to Seller & Host"
  // on the table row).
  const willHoldNotifications = existing
    ? !!existing.pendingSellerApproval
    : isOccupied;

  if (!open) return null;

  const handleSave = async () => {
    if (!dealId || !hostingAgentName || !hostingAgentEmail || !date || !startTime || !endTime) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!selectedDeal && !existing) {
      toast.error("Please choose a listing.");
      return;
    }
    setSaving(true);
    try {
      if (existing) {
        const timeOrAgentChanged =
          existing.date !== date ||
          existing.startTime !== startTime ||
          existing.endTime !== endTime ||
          existing.hostingAgentName !== hostingAgentName ||
          existing.hostingAgentEmail !== hostingAgentEmail;

        await updateOpenHouse(
          existing.id,
          { hostingAgentId, hostingAgentName, hostingAgentEmail, date, startTime, endTime, notes },
          user.id
        );
        toast.success("Open house updated.");

        // Skip update emails while the OH is still in seller-approval hold —
        // the seller hasn't received the original scheduling email yet, so
        // an "update" email would be incoherent.
        if (timeOrAgentChanged && !existing.pendingSellerApproval) {
          fireUpdateEmails(
            existing,
            { hostingAgentName, hostingAgentEmail, date, startTime, endTime, notes },
            user,
            emailTemplates
          ).catch((err) => console.error("Email send error:", err));
        }
        onSaved && onSaved({ ...existing, hostingAgentId, hostingAgentName, hostingAgentEmail, date, startTime, endTime, notes });
      } else {
        const saved = await createOpenHouse({
          deal: selectedDeal,
          fields: {
            hostingAgentId,
            hostingAgentName,
            hostingAgentEmail,
            date,
            startTime,
            endTime,
            notes,
            pendingSellerApproval: isOccupied,
          },
          userId: user.id,
        });
        if (isOccupied) {
          toast.success(
            "Open house saved — waiting for seller approval before notifying.",
          );
        } else {
          toast.success("Open house scheduled.");
          // Vacant home → fire scheduling emails immediately. The doc is
          // re-read inside sendSchedulingEmails to pick up feedbackToken etc.
          sendSchedulingEmails(saved.id, user, emailTemplates).catch((err) =>
            console.error("Email send error:", err),
          );
        }
        onSaved && onSaved(saved);
      }
      onClose();
    } catch (err) {
      console.error("ScheduleModal save error:", err);
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/55 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-xl shadow-2xl flex flex-col max-h-[calc(100vh-32px)]">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">
            {existing ? "Edit Open House" : "Schedule Open House"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-3">
          {willHoldNotifications && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">
              <span className="font-semibold">Home is occupied.</span> We'll
              save this open house but hold the seller & hosting-agent
              notifications until you confirm the seller OK'd the time. Use
              the <span className="font-semibold">Send to Seller &amp; Host</span>
              {" "}button on the open-houses list once they've approved.
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Listing</label>
            <select
              value={dealId}
              onChange={(e) => setDealId(e.target.value)}
              disabled={!!lockListing || !!existing}
              className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm disabled:opacity-70"
            >
              <option value="">— Select a listing —</option>
              {existing && (
                <option value={existing.dealId}>{existing.propertyAddress}</option>
              )}
              {lockListing && (
                <option value={lockListing.id}>
                  {lockListing.propertyaddress || lockListing.id}
                </option>
              )}
              {!existing && !lockListing && eligibleDeals.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.propertyaddress || d.id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Team Hosting Agent
            </label>
            <select
              value={hostingAgentId || "__outside__"}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "__outside__") {
                  setHostingAgentId("");
                  setHostingAgentName("");
                  setHostingAgentEmail("");
                  return;
                }
                const u = teamHosts.find((m) => m.id === val);
                if (u) setHostFromTeamMember(u);
              }}
              className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm"
            >
              <option value="__outside__">— Outside agent (type below) —</option>
              {teamHosts.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstname} {u.lastname}
                  {u.role === "Admin" ? " · Team Lead" : ""}
                </option>
              ))}
            </select>
          </div>

          {!hostingAgentId && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Outside Hosting Agent
                </label>
                <input
                  value={hostingAgentName}
                  onChange={(e) => {
                    const nextName = e.target.value;
                    setHostingAgentName(nextName);
                    // If the typed name exactly matches a previously-used
                    // outside agent, auto-fill their email so the user
                    // doesn't have to re-enter it.
                    const match = pastHosts.find(
                      (h) =>
                        h.name.trim().toLowerCase() ===
                        nextName.trim().toLowerCase(),
                    );
                    if (match) setHostingAgentEmail(match.email);
                  }}
                  list="oh-past-hosting-agents"
                  placeholder="Type a name (or pick a previously hosted agent)"
                  className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm"
                  autoComplete="off"
                />
                <datalist id="oh-past-hosting-agents">
                  {pastHosts.map((h) => (
                    <option key={h.email} value={h.name}>
                      {h.email}
                    </option>
                  ))}
                </datalist>
                {pastHosts.length > 0 && (
                  <p className="text-[11px] text-slate-400 mt-1">
                    Start typing to pick a previously hosted agent — email auto-fills.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Outside Hosting Agent Email
                </label>
                <input
                  type="email"
                  value={hostingAgentEmail}
                  onChange={(e) => setHostingAgentEmail(e.target.value)}
                  placeholder="agent@example.com"
                  className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm"
                />
              </div>
            </>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Start</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">End</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm"
              placeholder="Anything specific the hosting agent should know..."
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-slate-800 hover:bg-slate-900 rounded-md disabled:opacity-50"
          >
            {saving
              ? "Saving…"
              : existing
                ? "Save Changes"
                : willHoldNotifications
                  ? "Save (Hold for Seller)"
                  : "Schedule & Notify"}
          </button>
        </div>
      </div>
    </div>
  );
}

async function fireUpdateEmails(existingOH, newFields, user, templates) {
  const oldFields = {
    date: existingOH.date,
    startTime: existingOH.startTime,
    endTime: existingOH.endTime,
  };
  const updatedOH = { ...existingOH, ...newFields };

  if (existingOH.sellerEmail) {
    const { subject, text, html } = composeUpdateSellerEmail(updatedOH, oldFields, newFields, user, templates?.["update-seller"]);
    sendOpenHouseEmail({
      userId: user.id,
      to: existingOH.sellerEmail,
      toName: existingOH.sellerName,
      subject,
      text,
      html,
      type: "update",
      relatedDealId: existingOH.dealId,
      relatedOpenHouseId: existingOH.id,
    }).then((r) => {
      if (!r.ok) toast.warn(`Seller update email failed: ${r.error}`);
    });
  }
  if (newFields.hostingAgentEmail) {
    const { subject, text, html } = composeUpdateAgentEmail(updatedOH, oldFields, newFields, user, undefined, templates?.["update-agent"]);
    sendOpenHouseEmail({
      userId: user.id,
      to: newFields.hostingAgentEmail,
      toName: newFields.hostingAgentName,
      subject,
      text,
      html,
      type: "update",
      relatedDealId: existingOH.dealId,
      relatedOpenHouseId: existingOH.id,
    }).then((r) => {
      if (!r.ok) toast.warn(`Agent update email failed: ${r.error}`);
    });
  }
  {
    const { subject, text, html } = composeUpdateAgentEmail(
      updatedOH,
      oldFields,
      newFields,
      user,
      undefined,
      templates?.["update-agent"],
    );
    sendListingAgentCopy({
      oh: updatedOH,
      sender: user,
      subject,
      text,
      html,
      type: "update",
      excludeEmails: [existingOH.sellerEmail, newFields.hostingAgentEmail],
    }).catch((err) =>
      console.error("Listing-agent update copy failed:", err),
    );
  }
}
